import {GetServerSideProps} from "next";
import {ReactElement, useCallback, useMemo} from "react";
import type {NextPageWithLayout} from "./_app";
import {Layout} from "@components/layouts/layout";
import {getDb} from "../server/api/db";
import Link from "@components/elements/DocumentLink";
import {TextField} from "@components/elements/TextField";
import {StarRating} from "@components/elements/StarRating";
import {PageNavigation} from "@components/elements/PageNavigation";
import {formatNumber} from "@utils/format";
import {navigateToDocument} from "@utils/documentNavigation";

type LanguageDirectoryItem = {
    slug: string;
    english_name: string;
    native_name: string;
    plugins_count: number;
    total_ratings: number;
    average_rating: number | null;
};

type LanguagesDirectoryPageProps = {
    q: string;
    page: number;
    perPage: number;
    totalItems: number;
    items: LanguageDirectoryItem[];
};

const clampPage = (value: number): number => {
    if (!Number.isFinite(value)) return 1;
    return Math.max(1, Math.floor(value));
};

const normalizeQuery = (value: unknown): string => {
    if (Array.isArray(value)) return String(value[0] ?? "").trim();
    return String(value ?? "").trim();
};

const getBaseUrl = (): string => {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

const buildDirectoryUrl = (basePath: string, q: string, page: number): string => {
    const query = new URLSearchParams();
    if (q) query.set("q", q);
    if (page > 1) query.set("page", String(page));
    const suffix = query.toString();
    return suffix ? `${basePath}?${suffix}` : basePath;
};

export const getServerSideProps: GetServerSideProps<LanguagesDirectoryPageProps> = async (ctx) => {
    ctx.res?.setHeader("Cache-Control", "public, s-maxage=2592000, stale-while-revalidate=604800");

    const q = normalizeQuery(ctx.query.q).slice(0, 100);
    const page = clampPage(Number(normalizeQuery(ctx.query.page) || 1));
    const perPage = 200;

    const db = getDb();
    const qLike = q ? `%${q.toLowerCase()}%` : "";

    const countResult = await db.oneOrNone(
        `SELECT COUNT(DISTINCT l.languageid)::int AS count
         FROM language l
         JOIN pluginlanguage pl ON pl.languageid = l.languageid
         WHERE $<q> = ''
            OR l.languageid ILIKE $<qLike>
            OR LOWER(COALESCE(l.name, '')) LIKE $<qLike>
            OR LOWER(COALESCE(l.namenative, '')) LIKE $<qLike>`,
        {q, qLike},
    );

    const totalItems = Number(countResult?.count ?? 0);
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const normalizedPage = Math.min(page, totalPages);

    if (page !== normalizedPage) {
        const destination = q
            ? `/languages?page=${normalizedPage}&q=${encodeURIComponent(q)}`
            : `/languages?page=${normalizedPage}`;
        return {redirect: {destination, permanent: false}};
    }

    const offset = (normalizedPage - 1) * perPage;
    const rows = (await db.query(
        `SELECT l.languageid AS slug,
                COALESCE(NULLIF(l.name, ''), l.languageid) AS english_name,
                COALESCE(NULLIF(l.namenative, ''), '') AS native_name,
                COUNT(pl.pluginid)::int AS plugins_count,
                SUM(COALESCE(plugins.num_ratings, 0))::int AS total_ratings,
                (SUM(plugins.rating::numeric * COALESCE(plugins.num_ratings, 0))
                    / NULLIF(SUM(COALESCE(plugins.num_ratings, 0)), 0)
                    / 20.0) AS average_rating
         FROM language l
         JOIN pluginlanguage pl ON pl.languageid = l.languageid
         JOIN plugins_full_view AS plugins ON plugins.pluginid = pl.pluginid
         WHERE $<q> = ''
            OR l.languageid ILIKE $<qLike>
            OR LOWER(COALESCE(l.name, '')) LIKE $<qLike>
            OR LOWER(COALESCE(l.namenative, '')) LIKE $<qLike>
         GROUP BY l.languageid, l.name, l.namenative
        ORDER BY plugins_count DESC, slug
         LIMIT $<limit> OFFSET $<offset>`,
        {q, qLike, limit: perPage, offset},
    )) as unknown[];

    const items: LanguageDirectoryItem[] = (rows ?? []).map((row: any) => ({
        slug: String(row.slug ?? "").trim(),
        english_name: String(row.english_name ?? row.slug ?? "").trim(),
        native_name: String(row.native_name ?? "").trim(),
        plugins_count: Number(row.plugins_count ?? 0),
        total_ratings: Number(row.total_ratings ?? 0),
        average_rating: row.average_rating === null || row.average_rating === undefined ? null : Number(row.average_rating),
    })).filter((item) => Boolean(item.slug));

    return {
        props: {
            q,
            page: normalizedPage,
            perPage,
            totalItems,
            items,
        },
    };
};

const LanguagesDirectoryPage: NextPageWithLayout<LanguagesDirectoryPageProps> = (props) => {
    const totalPages = useMemo(() => Math.max(1, Math.ceil(props.totalItems / props.perPage)), [props.totalItems, props.perPage]);

    const updateQuery = useCallback((next: {page?: number; q?: string}) => {
        const nextQ = (next.q ?? props.q).trim();
        const nextPage = clampPage(next.page ?? props.page);
        navigateToDocument(buildDirectoryUrl("/languages", nextQ, nextPage));
    }, [props.page, props.q]);

    return (
        <div className="relative overflow-hidden">
            <div
                className="pointer-events-none absolute inset-x-0 top-[-12rem] h-64 bg-gradient-to-br from-indigo-100 via-sky-50 to-transparent opacity-70 blur-3xl dark:from-indigo-900/50 dark:via-slate-900 dark:to-transparent"
                aria-hidden="true"
            />
            <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-8 md:px-8 md:py-12">
                <header className="rounded-3xl border border-gray-200 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/60 md:p-8">
                    <div className="flex flex-col gap-4">
                        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                            Directory
                        </div>
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div className="space-y-1">
                                <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
                                    WordPress plugin languages
                                </h1>
                                <p className="max-w-2xl text-gray-700 dark:text-gray-300">
                                    Find translated plugins and localization packs across every supported language.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                                    {formatNumber(props.totalItems)} languages
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                                    Page {formatNumber(props.page)} of {formatNumber(totalPages)}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="flex w-full max-w-2xl items-center gap-3">
                                <TextField
                                    id="languages-search"
                                    value={props.q}
                                    placeholder="Search languages (code, English, native)…"
                                    onChanged={(value) => updateQuery({q: value, page: 1})}
                                    className="w-full"
                                />
                                {props.q && (
                                    <Link
                                        href="/languages"
                                        className="text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                    >
                                        Clear
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {props.items.map((language) => {
                        const label = language.english_name || language.slug;
                        const nativeSuffix = language.native_name && language.native_name !== label ? ` • ${language.native_name}` : "";

                        return (
                            <div
                                key={language.slug}
                                className="group rounded-2xl border border-gray-200 bg-white/80 p-5 shadow-sm transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-indigo-500/60"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <Link
                                        href={`/language/${language.slug}`}
                                        prefetch={false}
                                        className="text-lg font-semibold text-gray-900 transition group-hover:text-indigo-700 dark:text-gray-100 dark:group-hover:text-indigo-300"
                                    >
                                        {label}{nativeSuffix}
                                    </Link>
                                    <span className="rounded-full bg-indigo-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                                        {formatNumber(language.plugins_count)} plugins
                                    </span>
                                </div>
                                <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    Slug <span className="font-mono text-gray-800 dark:text-gray-200">{language.slug}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    {language.total_ratings > 0 && language.average_rating !== null ? (
                                        <>
                                            <StarRating rating={language.average_rating} maxRating={5} starSizeClassName="w-4 h-4"/>
                                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                                                {formatNumber(language.average_rating, {minimumFractionDigits: 1, maximumFractionDigits: 1})}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                ({formatNumber(language.total_ratings)} ratings)
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">No ratings yet</span>
                                    )}
                                </div>
                                <div className="mt-3 text-sm">
                                    <Link
                                        href={`/language/${language.slug}`}
                                        prefetch={false}
                                        className="inline-flex items-center gap-1 font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                    >
                                        View language page
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </section>

                <div className="flex justify-center">
                    <PageNavigation
                        className="mt-2"
                        currentPage={props.page}
                        totalPages={totalPages}
                        totalItems={props.totalItems}
                        onPageChange={(nextPage) => updateQuery({page: nextPage})}
                        getPageHref={(nextPage) => buildDirectoryUrl("/languages", props.q, nextPage)}
                    />
                </div>
            </div>
        </div>
    );
};

LanguagesDirectoryPage.getLayout = function getLayout(page: ReactElement, props: LanguagesDirectoryPageProps) {
    const baseUrl = getBaseUrl();
    const query = new URLSearchParams();
    if (props.q) query.set("q", props.q);
    if (props.page > 1) query.set("page", String(props.page));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const canonicalUrl = baseUrl ? `${baseUrl}/languages${suffix}` : `/languages${suffix}`;
    const imageUrl = baseUrl ? `${baseUrl}/catspyjamas.jpg` : "/catspyjamas.jpg";

    return (
        <Layout
            title="WordPress Plugin Languages"
            description="Browse WordPress plugin language packs and find plugins translated for your audience."
            url={canonicalUrl}
            image={imageUrl}
        >
            {page}
        </Layout>
    );
};

export default LanguagesDirectoryPage;
